import { Constant } from '@app/const';
import { Autocomplete, PopUp } from '@app/form_components';
import { InputIcon } from '@platform/ui-library';
import { ChangeEvent, useMemo, useState } from 'react';
import { ButtonConfig, ButtonFormProps } from './types';

export function ButtonForm({
  selectedItem,
  onSubmit,
  onRemoveItem,
  onClose,
  multilangPrefix,
  refRect
}: ButtonFormProps) {
  const [selectedType, setSelectedType] = useState(selectedItem?.urlType || 'value');
  const [url, setUrl] = useState(selectedItem?.url || '');

  const intents = window.apm.serialized.components.intents;
  const intentOptions = intents.reduce((acc: Record<string, string>, intent) => {
    acc[intent.id] = intent.name;
    return acc;
  }, {});

  const languageCodes = window.apm.serialized.languages.map(
    ({ language }) => language
  );

  const types: { value: string; name: string; }[] =
    window.apm.knowledgeBase.getNodeInputTypes();
  const typeOptions = types.reduce(
    (acc: Record<string, string>, { value, name }) => {
      acc[value] = name;
      return acc;
    },
    {}
  );

  const initialState: ButtonConfig = useMemo(
    () => ({
      id: selectedItem?.id || '',
      title: selectedItem?.title || '',
      label: languageCodes.reduce(
        (acc, language) => ({
          ...acc,
          [language]: selectedItem?.label?.[language] || ''
        }),
        {}
      ),
      urlType: selectedItem?.urlType || Object.keys(typeOptions)[0],
      url: selectedItem?.url || '',
      intent: selectedItem?.intent || ''
    }),
    [selectedItem]
  );

  return (
    <PopUp handleClose={onClose} refRect={refRect}>
      <form onSubmit={onSubmit}>
        <fieldset>
          <InputIcon
            label="Intent"
            options={intentOptions}
            type="select"
            placeholder="- Select an Intent -"
            onChange={() => null}
            defaultValue={initialState.intent}
            name="intent"
            required
          />

          {Object.keys(initialState.label).map((language, i) => (
            <InputIcon
              key={language}
              label={window.apm.getLanguage(language)?.name}
              name={`${multilangPrefix}${language}`}
              defaultValue={initialState.label[language]}
              required={i === 0}
            />
          ))}

          <InputIcon
            label="Type"
            options={typeOptions}
            type="select"
            defaultValue={initialState.urlType}
            name="urlType"
            required
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSelectedType(e.target.value)}
          />

          {selectedType === 'value' && (
            <InputIcon
              label="URL"
              placeholder="Enter URL (optional)"
              defaultValue={initialState.url}
              name="url"
            />
          )}

          {selectedType === 'variable' && (
            <div>
              <label>URL</label>
              <Autocomplete<string>
                getOptions={getVariableOptions}
                minSearchLength={2}
                labels={{
                  isLoading: 'Searching variables...',
                  notFound: 'No variables found.',
                  title: 'Variable'
                }}
                onValueChange={(val) => setUrl(val)}
                value={url}
                initSearchValue={url}
              />
              <input type="hidden" name="url" value={url} />
            </div>
          )}
        </fieldset>
        <div className='actions'>
          {selectedItem?.id && (
            <button
              type="button"
              onClick={() => onRemoveItem(selectedItem.id)}
              className="form-button normal warning"
              title="Remove"
            >
              <i className="fe icon-trash"></i>
            </button>
          )}

          <button type="submit" className="form-button normal">
            Submit
          </button>
        </div>
      </form>
    </PopUp>
  );
}

function getVariableOptions(searchString: string) {
  const variables = window.apm.knowledgeBase.getVariableSuggestions(
    searchString,
    Constant.k.SCOPE_APPLICATION
  );
  if (!variables?.app?.length) {
    return [];
  }
  return variables.app.map((variable) => {
    const { varName } = variable.props.config;
    return {
      label: varName,
      value: `app.${varName}`
    };
  });
}
